import React, { useState } from "react";

import { useRouter } from "next/router";

import { t, Trans } from "@lingui/macro";
import { Alert, Button, Col, Form, message, Row, Space } from "antd";
import { isEmpty } from "lodash";
import styled from "styled-components";

import DebouncedSearch from "components/debounced-search";
import MoreButton from "components/more-button";
import { ActionMenu } from "components/navigations/header";
import { PermissionDetails } from "components/permissions/edit-link";
import { useFetchRecords } from "components/permissions/hooks";
import PermissionsTable from "components/permissions/table";
import { generateRecords, mergePermissions, transformPermissionsToList } from "components/permissions/utils";
import { pageCountMessage } from "components/table";
import ButtonTabs from "components/uikit/button-tabs";

import { useApplicationContext } from "contexts/app";

import { Link } from "types";

import useOptions from "hooks/options";
import useQueryParams from "hooks/query-params";

import { DEFAULT_PAGE_SIZE } from "utils/http-client-utils";

interface ManagePermissionsProps {
  title: string;
  titleIndex: string;
  canManage?: boolean;
  permissionPath: string;
  recordsPath: string;
  iteratee: string;
  tabsTitle: { permissions: string; allPermissions: string };
  permissionDetails: PermissionDetails;
  offSetTop?: number;
  links?: Link[];
}

const Container = styled.div`
  padding: 8px 8px 0 24px;
`;

const ManagePermissions = ({
  titleIndex,
  canManage,
  permissionPath,
  recordsPath,
  iteratee,
  tabsTitle,
  permissionDetails,
  offSetTop = 0,
  links,
}: ManagePermissionsProps) => {
  const [updatedPermission, setUpdatePermissions] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [changed, setChanged] = useState(false);
  const [form] = Form.useForm();

  const { session, httpClient } = useApplicationContext();
  const router = useRouter();
  const setURLSearchParams = useQueryParams();

  const { permission } = router.query;
  const params = {
    account: session?.accountId,
    managed_permissions: "true",
    page_size: DEFAULT_PAGE_SIZE,
    active: "true",
  };
  const permissionsParams = {
    page_size: DEFAULT_PAGE_SIZE,
  };

  const { records, mutate: mutateUsers, loading } = useFetchRecords(recordsPath, params, canManage);
  const {
    records: permissions,
    mutate: mutatePermissions,
    loading: loadingPermissions,
  } = useFetchRecords(permissionPath, permissionsParams, canManage);
  const { result: options, loading: loadingColumns } = useOptions(permissionPath, "actions.POST.permissions.children");

  const withPermission = Boolean(permission);
  const mergedPermissions = mergePermissions(permissions, records, updatedPermission, iteratee);
  const recordsWithPermissions = generateRecords(mergedPermissions, records, iteratee);

  const count = withPermission
    ? recordsWithPermissions.filter((record) => {
        return Object.values(record.permissions)?.some((p: any) => {
          return p?.has_permission === true;
        });
      })?.length
    : recordsWithPermissions.length;

  const savePermission = async () => {
    const payload = transformPermissionsToList(updatedPermission, iteratee);

    if (payload) {
      setSubmitting(true);
    }

    await httpClient?.chunkedPost(permissionPath, {
      data: payload,
      successHandler: () => {
        message.success(t`Permissions updated`);

        mutatePermissions();
        mutateUsers();
        setUpdatePermissions({});
        setSubmitting(false);
      },
    });
    return;
  };

  return (
    <Row gutter={[0, 16]}>
      <ActionMenu
        menus={[
          changed && (
            <Button
              type="default"
              onClick={() => {
                setUpdatePermissions({});
                setChanged(false);
              }}
            >
              <Trans>Discard</Trans>
            </Button>
          ),
          <Button
            type="primary"
            onClick={() => {
              form.submit();
            }}
          >
            <Trans>Save</Trans>
          </Button>,
          !isEmpty(links) ? (
            <MoreButton
              items={links?.map((link: any) => {
                return {
                  key: 0,
                  label: link.title,
                  onClick: async () => {
                    await router.push(link.url);
                  },
                };
              })}
            />
          ) : null,
        ]}
      />

      <Col span={24}>
        <Container>
          <Space size={16}>
            <DebouncedSearch placeholder={t`Search...`} allowClear />
            <ButtonTabs
              tabs={[
                {
                  key: "permission",
                  label: tabsTitle.permissions,
                },
                {
                  key: "all-groups",
                  label: tabsTitle.allPermissions,
                },
              ]}
              activeTab={permission ? "permission" : "all-groups"}
              onChange={(key) => {
                if (key === "all-groups") {
                  setURLSearchParams([{ action: "delete", key: "permission" }]);
                } else setURLSearchParams([{ action: "set", key: "permission", value: "true" }]);
              }}
            />
            <div>{pageCountMessage(t`Records`)(count)}</div>
          </Space>
        </Container>
      </Col>
      {!canManage || (!options && !loadingColumns) ? (
        <Col span={24}>
          <Alert message={t`You do not have the permissions to manage this permissions`} banner type={"warning"} />
        </Col>
      ) : (
        <Col span={24}>
          <Form
            form={form}
            onFinish={async () => {
              await savePermission();
            }}
          >
            <PermissionsTable
              permissionDetails={permissionDetails}
              updatedPermission={updatedPermission}
              setUpdatePermissions={(v) => {
                setChanged(true);
                setUpdatePermissions(v);
              }}
              columns={options}
              records={{ permissions, owners: records, iteratee }}
              withPermission={withPermission}
              dataIndex={titleIndex}
              loading={loading || loadingPermissions || loadingColumns || submitting}
              offSetTop={offSetTop}
            />
          </Form>
        </Col>
      )}
    </Row>
  );
};

export default ManagePermissions;
